(() => {
  const MAX_FREE = 4;
  const DEBUG = '[SampleEnforcer]';
  const ENFORCE_DELAY_MS = 400;
  const SOFT_LOCK_TTL_MS = 3000;

  let isRunning = false;
  let scheduled = null;

  const log = (...a) => console.log(DEBUG, ...a);
  const err = (...a) => console.error(DEBUG, ...a);

  /* =======================
     Soft lock
  ======================= */
  const LOCK_KEY = '__sample_enforcer_lock__';

  const lock = () => {
    try {
      sessionStorage.setItem(LOCK_KEY, Date.now() + SOFT_LOCK_TTL_MS);
    } catch (_) {}
  };

  const isLocked = () => {
    try {
      return Date.now() < Number(sessionStorage.getItem(LOCK_KEY) || 0);
    } catch {
      return false;
    }
  };

  /* =======================
     Network helpers
  ======================= */
  const getCart = () =>
    fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());

  const changeItem = (key, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });

  const addItem = (variantId, quantity) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity })
    });

  /* =======================
     api-cart-data
  ======================= */
  async function loadApiCartData() {
    const html = await fetch('/?section_id=api-cart-data', {
      cache: 'no-store'
    }).then(r => r.text());

    const el = document.createElement('div');
    el.innerHTML = html;
    const section = el.querySelector('#shopify-section-api-cart-data');
    if (!section) return {};

    try {
      return JSON.parse(section.textContent).items || {};
    } catch {
      return {};
    }
  }

  /* =======================
     Scheduler
  ======================= */
  function schedule(reason) {
    if (isLocked()) return;
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => enforce(reason), ENFORCE_DELAY_MS);
  }

  /* =======================
     Enforcement
  ======================= */
  async function enforce(reason) {
    if (isRunning || isLocked()) return;
    isRunning = true;

    console.group(`${DEBUG} enforce (${reason})`);

    try {
      const api = await loadApiCartData();
      const cart = await getCart();

      const groups = {};

      for (const item of cart.items) {
        const cfg = api[item.handle];
        if (!cfg?.is_sample) continue;

        const parent = cfg.parent_handle;
        groups[parent] ??= {
          samples: [],
          premiumVariantId: cfg.premium.variant_id
        };
        groups[parent].samples.push(item);
      }

      let shouldReload = false;
      let didChange = false;

      for (const parent in groups) {
        const { samples, premiumVariantId } = groups[parent];

        const freeQty = samples.reduce((s, i) => s + i.quantity, 0);
        const premiumItem = cart.items.find(i => i.handle === parent);
        const premiumQty = premiumItem ? premiumItem.quantity : 0;

        const wasOverLimit = freeQty > MAX_FREE;

        const total = freeQty + premiumQty;
        const allowedFree = Math.min(total, MAX_FREE);
        const requiredPremium = Math.max(total - MAX_FREE, 0);

        log(parent, {
          freeQty,
          premiumQty,
          allowedFree,
          requiredPremium,
          wasOverLimit
        });

        // Enforce free samples
        let remaining = allowedFree;
        for (const item of samples) {
          const newQty = Math.min(item.quantity, remaining);
          remaining -= newQty;

          if (newQty !== item.quantity) {
            await changeItem(item.key, newQty);
            didChange = true;
          }
        }

        // Enforce premium
        if (premiumItem) {
          if (premiumItem.quantity !== requiredPremium) {
            await changeItem(premiumItem.key, requiredPremium);
            didChange = true;
          }
        } else if (requiredPremium > 0) {
          await addItem(premiumVariantId, requiredPremium);
          didChange = true;
        }

        // 🔑 reload ONLY if cart started invalid
        if (wasOverLimit) {
          shouldReload = true;
        }
      }

      if (!didChange) {
        log('No changes needed');
        return;
      }

      if (shouldReload) {
        log('Over-limit detected → soft reload once');
        lock();
        window.location.reload();
      } else {
        log('Changes applied without reload');
      }

    } catch (e) {
      err('Enforcement error', e);
    } finally {
      console.groupEnd();
      isRunning = false;
    }
  }

  /* =======================
     Triggers
  ======================= */
  document.addEventListener('DOMContentLoaded', () => schedule('load'));

  const origFetch = window.fetch;
  window.fetch = async (...args) => {
    const res = await origFetch(...args);
    const url = args[0]?.toString() || '';
    if (url.includes('/cart/')) schedule(`fetch:${url}`);
    return res;
  };

  document.addEventListener(
    'change',
    e => {
      if (
        e.target?.matches(
          'input[name="updates[]"], input[data-quantity-input]'
        )
      ) {
        schedule('qty-change');
      }
    },
    true
  );

  document.addEventListener(
    'click',
    e => {
      if (e.target.closest('[data-add-quantity],[data-subtract-quantity]')) {
        schedule('qty-click');
      }
    },
    true
  );
})();
